<template>
  <CatalogoPagina
    titulo="Proveedores"
    prefijo="proveedores"
    :resumen="resumen"
    :columnas="columnas"
    :total="total"
    :pagina="paginaActual"
    :total-paginas="totalPaginas"
    :limite="limite"
    :cargando="cargando"
    :ya-cargo="yaCargo"
    :error="errorCarga"
    mensaje-error="No pudimos cargar los proveedores."
    :orden-campo="ordenCampo"
    :orden-asc="ordenAsc"
    :aviso="aviso"
    @limite="alCambiarLimite"
    @ordenar="ordenarPor"
    @pagina="cambiarPagina"
    @reintentar="initialize"
    @cerrar-aviso="aviso = ''"
  >
    <template #acciones>
      <button type="button" class="rm-btn rm-btn-primario" data-testid="proveedores-nuevo" @click="nuevoProveedor">
        <AppIcon name="plus" size="h-4 w-4" />
        Nuevo proveedor
      </button>
    </template>

    <template #filtros>
      <AppBusqueda
        v-model="busqueda"
        placeholder="Buscar proveedor"
        testid="proveedores-buscar"
        @buscar="aplicarFiltros"
      />
      <DateRangeFilter
        class="cat-filtro-fechas"
        label="Última compra"
        :desde="compraDesde"
        :hasta="compraHasta"
        :opciones="rangosCompra"
        testid="filtro-compra"
        @change="cambiarRangoCompra"
      />
      <button
        v-if="hayFiltros"
        type="button"
        class="cat-limpiar"
        data-testid="proveedores-limpiar-filtros"
        @click="limpiarFiltros"
      >
        Limpiar filtros
      </button>
    </template>

    <template #filas>
      <tr v-for="proveedor in proveedores" :key="proveedor._id" class="pv-fila" data-testid="proveedor-fila">
        <td class="pv-celda-proveedor">
          <div class="cat-nombre">
            <span class="cat-avatar" aria-hidden="true">{{ iniciales(proveedor) }}</span>
            <div class="cat-nombre-datos">
              <span class="cat-nombre-texto">{{ empresaDe(proveedor) }}</span>
              <span v-if="contactoDe(proveedor)" class="cat-meta">{{ contactoDe(proveedor) }}</span>
            </div>
          </div>
        </td>
        <td class="pv-celda-contacto">
          <div v-if="telefonosDe(proveedor.telephones).length || proveedor.email" class="cat-pila">
            <span v-if="telefonosDe(proveedor.telephones).length" class="pv-telefono">
              {{ telefonosDe(proveedor.telephones)[0] }}
              <span
                v-if="telefonosDe(proveedor.telephones).length > 1"
                class="pv-mas"
                :title="telefonosDe(proveedor.telephones).slice(1).join(' · ')"
              >+{{ telefonosDe(proveedor.telephones).length - 1 }}</span>
            </span>
            <span v-if="proveedor.email" class="cat-meta">{{ proveedor.email }}</span>
          </div>
          <span v-else class="cat-sin-dato" aria-label="Sin teléfono ni correo">—</span>
        </td>
        <td class="pv-celda-numero">
          <template v-if="proveedor.compras">{{ cifra(proveedor.compras) }}<span class="pv-unidad"> {{ proveedor.compras === 1 ? 'compra' : 'compras' }}</span></template>
          <span v-else class="cat-sin-dato">Sin compras</span>
        </td>
        <td class="pv-celda-numero pv-celda-fecha">
          <template v-if="proveedor.ultimaCompra"><span class="pv-unidad">Última: </span>{{ fecha(proveedor.ultimaCompra) }}</template>
          <span v-else class="cat-sin-dato" aria-label="Sin compras">—</span>
        </td>
        <td class="cat-fila-acciones">
          <button
            type="button"
            class="cat-icono-btn"
            :aria-label="'Editar ' + empresaDe(proveedor)"
            data-testid="proveedor-editar"
            @click="editarProveedor(proveedor)"
          >
            <AppIcon name="pencil" size="h-4 w-4" />
          </button>
          <button
            type="button"
            class="cat-icono-btn peligro"
            :aria-label="'Dar de baja a ' + empresaDe(proveedor)"
            data-testid="proveedor-eliminar"
            @click="pedirEliminar(proveedor)"
          >
            <AppIcon name="trash" size="h-4 w-4" />
          </button>
        </td>
      </tr>
    </template>

    <template #vacio>
      <AppIcon :name="hayFiltros ? 'search' : 'building'" size="h-8 w-8" />
      <p v-if="busqueda.trim() && !hayRangoCompra">No hay proveedores que coincidan con “{{ busqueda.trim() }}”.</p>
      <p v-else-if="hayFiltros">No hay proveedores que coincidan con los filtros.</p>
      <template v-else>
        <p>Aún no hay proveedores.</p>
        <button type="button" class="rm-btn rm-btn-primario" @click="nuevoProveedor">Registrar el primero</button>
      </template>
    </template>

    <BaseModal :visible="dialogoForm" :title="tituloForm" max-width="max-w-[720px]" persistent @close="cerrarFormulario">
      <form novalidate class="pv-form" data-testid="proveedor-form" @submit.prevent="guardarProveedor">
        <AppField
          v-model="form.company_name"
          class="pv-c2"
          label="Empresa"
          required
          uppercase
          placeholder="REFACCIONARIA GAMBOA"
          :error="mostrarError('company_name')"
          testid="proveedor-empresa"
          @blur="tocar('company_name')"
        />
        <AppField
          v-model="form.contact_name"
          class="pv-c2"
          label="Contacto"
          required
          placeholder="Juan Olmos"
          :error="mostrarError('contact_name')"
          testid="proveedor-contacto"
          @blur="alSalirDeContacto"
        />
        <AppField
          v-model="telefonoVisible"
          label="Teléfono"
          required
          type="tel"
          inputmode="numeric"
          maxlength="14"
          placeholder="(667) 319 0560"
          :format="mascaraTelefono"
          :error="mostrarError('telefono')"
          testid="proveedor-telefono"
          @blur="tocar('telefono')"
        />
        <AppField
          v-model="telefono2Visible"
          label="Otro teléfono"
          type="tel"
          inputmode="numeric"
          maxlength="14"
          placeholder="Opcional"
          :format="mascaraTelefono"
          :error="mostrarError('telefono2')"
          testid="proveedor-telefono2"
          @blur="tocar('telefono2')"
        />
        <AppField v-slot="{ controlId }" class="pv-c2" label="Correo" :error="mostrarError('email')" testid="proveedor-correo">
          <AppCorreo
            :id="controlId"
            v-model="form.email"
            :invalid="Boolean(mostrarError('email'))"
            testid="proveedor-correo"
            @blur="tocar('email')"
          />
        </AppField>
        <AppField
          v-model="form.address"
          class="pv-c2"
          label="Dirección"
          placeholder="Calle, número y colonia"
          testid="proveedor-direccion"
          @blur="form.address = capitalizarDireccion(form.address)"
        />
      </form>
      <template #footer>
        <button type="button" class="rm-btn rm-btn-secundario" @click="cerrarFormulario">Cancelar</button>
        <button
          type="button"
          class="rm-btn rm-btn-primario"
          :disabled="guardando"
          data-testid="proveedor-guardar"
          @click="guardarProveedor"
        >
          {{ guardando ? 'Guardando…' : 'Guardar' }}
        </button>
      </template>
    </BaseModal>

    <BaseModal :visible="dialogoEliminar" title="Dar de baja proveedor" @close="cerrarEliminar">
      <p class="cat-confirmacion">
        ¿Dar de baja a <b>{{ empresaDe(proveedorSeleccionado) }}</b>? Ya no aparecerá al registrar compras.
        <template v-if="proveedorSeleccionado && proveedorSeleccionado.compras">
          Sus {{ cifra(proveedorSeleccionado.compras) }} compras registradas no cambian.
        </template>
      </p>
      <template #footer>
        <button type="button" class="rm-btn rm-btn-secundario" @click="cerrarEliminar">Cancelar</button>
        <button
          type="button"
          class="rm-btn rm-btn-peligro"
          :disabled="guardando"
          data-testid="proveedor-confirmar-eliminar"
          @click="confirmarEliminar"
        >
          {{ guardando ? 'Dando de baja…' : 'Dar de baja' }}
        </button>
      </template>
    </BaseModal>
  </CatalogoPagina>
</template>

<script>
/*
 * Proveedores — catálogo migrado sobre CatalogoPagina.
 *
 * Paginación, búsqueda (empresa, contacto, correo o teléfono) y orden en el
 * backend (/api/provider/paginated). Lo más usado primero: por número de
 * compras registradas, con la fecha de la última; se filtra por el rango de
 * esa última compra (como la última visita en Clientes). Teléfonos con formato
 * (667) 319 0560; los registros viejos con dos números en un texto se
 * muestran por separado y al editarlos se guardan en dos renglones.
 * Dar de baja es lógico (status 0): las compras anteriores no cambian.
 */
import ApiService from '@/services/apis'
import AppBusqueda from '@/components/ui/AppBusqueda.vue'
import AppCorreo from '@/components/ui/AppCorreo.vue'
import AppField from '@/components/ui/AppField.vue'
import AppIcon from '@/components/ui/AppIcon.vue'
import BaseModal from '@/components/ui/BaseModal.vue'
import CatalogoPagina, { LIMITE_BASE } from '@/components/catalogo/CatalogoPagina.vue'
import DateRangeFilter from '@/components/ui/DateRangeFilter.vue'
import { RANGOS_COMPRA } from '@/dashboard/calendar'
import { validateNoErrors } from '@/helpers/helpers'
import {
  capitalizarDireccion,
  capitalizarNombre,
  formatearFechaNumerica,
  mascaraTelefono,
  nombreDeEmpresa,
  telefonosDe,
} from '@/utils/formato'
import { paginaEquivalente } from '@/utils/paginacion'

const formatoCifra = new Intl.NumberFormat('es-MX')

function formularioVacio() {
  return { company_name: '', contact_name: '', telefono: '', telefono2: '', email: '', address: '' }
}

/* Teléfonos guardados → solo dígitos, uno por número ("a, b" se separa) */
function digitosDeTelefonos(valor) {
  const lista = Array.isArray(valor) ? valor : [valor]
  return lista
    .flatMap((t) => String(t ?? '').split(/[,;/]|\s{2,}/))
    .map((t) => t.replace(/\D/g, ''))
    .filter(Boolean)
}

export default {
  name: 'proveedores',
  components: { AppBusqueda, AppCorreo, AppField, AppIcon, BaseModal, CatalogoPagina, DateRangeFilter },
  data() {
    return {
      proveedores: [],
      busqueda: '',
      rangosCompra: RANGOS_COMPRA,
      compraDesde: '',
      compraHasta: '',
      ordenCampo: 'compras',
      ordenAsc: false,
      paginaActual: 1,
      total: 0,
      totalPaginas: 1,
      limite: LIMITE_BASE,
      cargando: true,
      yaCargo: false,
      errorCarga: false,
      guardando: false,
      dialogoForm: false,
      dialogoEliminar: false,
      proveedorSeleccionado: null,
      form: formularioVacio(),
      tocados: {},
      intentoGuardar: false,
      aviso: '',
    }
  },
  computed: {
    columnas() {
      return [
        { value: 'company_name', label: 'Proveedor' },
        { value: 'contacto', label: 'Teléfono y correo', ordenable: false },
        { value: 'compras', label: 'Compras', ancho: '130px', alinear: 'derecha' },
        { value: 'ultimaCompra', label: 'Última compra', ancho: '150px', alinear: 'derecha' },
      ]
    },
    hayRangoCompra() {
      return Boolean(this.compraDesde || this.compraHasta)
    },
    hayFiltros() {
      return Boolean(this.busqueda.trim()) || this.hayRangoCompra
    },
    resumen() {
      if (this.hayFiltros) return `${this.cifra(this.total)} ${this.total === 1 ? 'coincidencia' : 'coincidencias'}`
      return `${this.cifra(this.total)} proveedores activos`
    },
    tituloForm() {
      return this.proveedorSeleccionado ? 'Editar proveedor' : 'Nuevo proveedor'
    },
    telefonoVisible: {
      get() {
        return mascaraTelefono(this.form.telefono)
      },
      set(valor) {
        this.form.telefono = String(valor).replace(/\D/g, '')
      },
    },
    telefono2Visible: {
      get() {
        return mascaraTelefono(this.form.telefono2)
      },
      set(valor) {
        this.form.telefono2 = String(valor).replace(/\D/g, '')
      },
    },
    errores() {
      const f = this.form
      const e = {}
      if (!f.company_name.trim()) e.company_name = 'Requerido.'
      if (!f.contact_name.trim()) e.contact_name = 'Requerido.'
      if (!f.telefono) e.telefono = 'Requerido.'
      else if (!validateNoErrors('telephones', f.telefono)) e.telefono = 'Deben ser 10 dígitos.'
      if (f.telefono2 && !validateNoErrors('telephones', f.telefono2)) e.telefono2 = 'Deben ser 10 dígitos.'
      if (f.email && !validateNoErrors('email', f.email)) e.email = 'Formato no válido.'
      return e
    },
  },
  created() {
    this.ultimaConsulta = 0
    this.limiteListo = false
  },
  beforeDestroy() {
    clearTimeout(this.avisoTimer)
  },
  methods: {
    capitalizarDireccion,
    mascaraTelefono,
    telefonosDe,
    cifra(n) {
      return formatoCifra.format(n)
    },
    fecha(valor) {
      return formatearFechaNumerica(valor)
    },
    empresaDe(proveedor) {
      return proveedor ? nombreDeEmpresa(proveedor.company_name) : ''
    },
    contactoDe(proveedor) {
      return capitalizarNombre(String(proveedor.contact_name || '').trim())
    },
    iniciales(proveedor) {
      const palabras = this.empresaDe(proveedor).split(' ').filter((p) => /\p{L}/u.test(p))
      return palabras.slice(0, 2).map((p) => p[0]).join('').toUpperCase()
    },
    async initialize() {
      const consulta = ++this.ultimaConsulta
      this.cargando = true
      this.errorCarga = false
      try {
        const res = await ApiService.proveedorPaginado({
          pagina: this.paginaActual,
          limite: this.limite,
          busqueda: this.busqueda.trim(),
          compraDesde: this.compraDesde,
          compraHasta: this.compraHasta,
          // Días locales: el backend corta la última compra en hora de aquí
          zonaHoraria: new Date().getTimezoneOffset(),
          ordenarPor: this.ordenCampo,
          direccion: this.ordenAsc ? 'asc' : 'desc',
        })
        if (consulta !== this.ultimaConsulta) return
        if (!Array.isArray(res?.proveedores)) throw new Error('Respuesta inválida del servicio')
        this.proveedores = res.proveedores
        this.total = Number(res.total) || 0
        this.totalPaginas = Math.max(1, Number(res.totalPages) || 1)
        this.yaCargo = true
        if (this.paginaActual > this.totalPaginas) {
          this.paginaActual = this.totalPaginas
          return this.initialize()
        }
      } catch (err) {
        if (consulta !== this.ultimaConsulta) return
        this.errorCarga = true
        this.yaCargo = false
        this.proveedores = []
        this.total = 0
      } finally {
        if (consulta === this.ultimaConsulta) this.cargando = false
      }
    },
    alCambiarLimite(nuevo) {
      if (!this.limiteListo) {
        this.limiteListo = true
        this.limite = nuevo
        return this.initialize()
      }
      if (nuevo === this.limite) return
      this.paginaActual = paginaEquivalente(this.paginaActual, this.limite, nuevo)
      this.limite = nuevo
      this.initialize()
    },
    ordenarPor(campo) {
      if (campo === this.ordenCampo) {
        this.ordenAsc = !this.ordenAsc
      } else {
        this.ordenCampo = campo
        // La empresa empieza de la A; compras y fechas, de lo más reciente o usado
        this.ordenAsc = campo === 'company_name'
      }
      this.aplicarFiltros()
    },
    cambiarRangoCompra({ desde, hasta }) {
      if (desde === this.compraDesde && hasta === this.compraHasta) return
      this.compraDesde = desde
      this.compraHasta = hasta
      this.aplicarFiltros()
    },
    aplicarFiltros() {
      this.paginaActual = 1
      this.initialize()
    },
    limpiarFiltros() {
      this.busqueda = ''
      this.compraDesde = ''
      this.compraHasta = ''
      this.aplicarFiltros()
    },
    cambiarPagina(pagina) {
      if (pagina < 1 || pagina > this.totalPaginas || pagina === this.paginaActual) return
      this.paginaActual = pagina
      this.initialize()
    },
    abrirFormulario(proveedor) {
      this.proveedorSeleccionado = proveedor
      if (proveedor) {
        const [telefono = '', telefono2 = ''] = digitosDeTelefonos(proveedor.telephones)
        this.form = {
          company_name: String(proveedor.company_name || '').trim(),
          contact_name: this.contactoDe(proveedor),
          telefono,
          telefono2,
          email: proveedor.email || '',
          address: capitalizarDireccion(proveedor.address || ''),
        }
      } else {
        this.form = formularioVacio()
      }
      this.tocados = {}
      this.intentoGuardar = false
      this.dialogoForm = true
    },
    nuevoProveedor() {
      this.abrirFormulario(null)
    },
    editarProveedor(proveedor) {
      this.abrirFormulario(proveedor)
    },
    cerrarFormulario() {
      this.dialogoForm = false
      this.proveedorSeleccionado = null
    },
    tocar(campo) {
      this.tocados = { ...this.tocados, [campo]: true }
    },
    mostrarError(campo) {
      return this.tocados[campo] || this.intentoGuardar ? this.errores[campo] || '' : ''
    },
    alSalirDeContacto() {
      this.form.contact_name = capitalizarNombre(this.form.contact_name)
      this.tocar('contact_name')
    },
    async guardarProveedor() {
      this.intentoGuardar = true
      if (Object.keys(this.errores).length || this.guardando) return
      const f = this.form
      const payload = {
        company_name: f.company_name.trim().replace(/\s+/g, ' ').toUpperCase(),
        contact_name: capitalizarNombre(f.contact_name),
        telephones: [f.telefono, f.telefono2].filter(Boolean),
        email: f.email.trim(),
        address: capitalizarDireccion(f.address),
      }
      const editando = Boolean(this.proveedorSeleccionado)
      this.guardando = true
      try {
        if (editando) await ApiService.proveedorEditar(this.proveedorSeleccionado._id, payload)
        else await ApiService.proveedorNuevo(payload)
        this.cerrarFormulario()
        await this.initialize()
        this.mostrarAviso(editando ? 'Proveedor actualizado' : 'Proveedor agregado')
      } catch (err) {
        // El servicio ya muestra el motivo; el formulario sigue abierto
      } finally {
        this.guardando = false
      }
    },
    pedirEliminar(proveedor) {
      this.proveedorSeleccionado = proveedor
      this.dialogoEliminar = true
    },
    cerrarEliminar() {
      this.dialogoEliminar = false
      this.proveedorSeleccionado = null
    },
    async confirmarEliminar() {
      this.guardando = true
      try {
        await ApiService.proveedorEliminar(this.proveedorSeleccionado._id)
        const nombre = this.empresaDe(this.proveedorSeleccionado)
        this.cerrarEliminar()
        if (this.proveedores.length === 1 && this.paginaActual > 1) this.paginaActual--
        await this.initialize()
        this.mostrarAviso(`${nombre} dado de baja`)
      } catch (err) {
        // El servicio ya muestra el motivo
      } finally {
        this.guardando = false
      }
    },
    mostrarAviso(mensaje) {
      this.aviso = mensaje
      clearTimeout(this.avisoTimer)
      this.avisoTimer = setTimeout(() => {
        this.aviso = ''
      }, 4500)
    },
  },
}
</script>

<style scoped>
.pv-form {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px 14px;
}
.pv-form > * {
  grid-column: span 2;
}
.pv-form > .pv-c2 {
  grid-column: span 2;
}
.cat-pagina .cat-tabla td.pv-celda-numero {
  color: #3d4b5f;
  font-variant-numeric: tabular-nums;
  text-align: right;
  white-space: nowrap;
}
.pv-telefono {
  color: #0b1b2e;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.pv-mas {
  margin-left: 4px;
  padding: 1px 6px;
  border-radius: 999px;
  background: #edf3f8;
  color: #3d4b5f;
  font-size: 11px;
  font-weight: 700;
}
/* En escritorio la unidad la dice el encabezado; en tarjetas (móvil) no hay */
.pv-unidad {
  display: none;
}
@media (max-width: 700px) {
  .pv-form {
    grid-template-columns: minmax(0, 1fr);
  }
  .pv-form > *,
  .pv-form > .pv-c2 {
    grid-column: 1 / -1;
  }
  .cat-pagina .cat-tabla td.pv-celda-numero {
    font-size: 12px;
    text-align: left;
  }
  .pv-unidad {
    display: inline;
  }
}
</style>
